"use client";

import Image from "next/image";
import { ArrowDownRight, ArrowUpRight, Check, FlaskConical, Play, Sparkles } from "lucide-react";
import { useId, useState } from "react";
import type { MetaAdsSubpageCopy } from "./meta-ads-page-copy";
import styles from "./meta-ads-studio.module.scss";

const logos = {
  instagram: "/assets/imgs/tools/instagram.svg",
  facebook: "/assets/imgs/tools/facebook.svg",
  meta: "/assets/imgs/tools/meta.svg",
};

/** A local creative comparison, with the page's original illustrative data. */
export function CcMetaAdsCreativeBoard({ copy }: { copy: MetaAdsSubpageCopy["creativeBoard"] }) {
  const [selected, setSelected] = useState(0);
  const panelId = useId();
  const concepts = [copy.winner, copy.challenger];
  return <div className={styles.board}>
    <header className={styles.boardTop}>
      <Image src={logos.meta} alt="" width={24} height={24} unoptimized />
      <div><small>{copy.eyebrow}</small><strong>{copy.platform}</strong></div>
      <span className={styles.live}><i aria-hidden="true" />{copy.status}</span>
    </header>
    <div className={styles.boardCampaign}><span>{copy.campaign}</span><span>{copy.budgetLabel}<strong>{copy.budgetValue}</strong></span></div>
    <div className={styles.conceptControls} role="group" aria-label={copy.eyebrow}>
      {concepts.map((concept, index) => <button type="button" key={concept.badge} aria-pressed={selected === index} aria-controls={panelId} onClick={() => setSelected(index)}>
        {index === 0 ? <Sparkles size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}{concept.badge}
      </button>)}
    </div>
    <div id={panelId} className={styles.conceptPanels} aria-live="polite" aria-atomic="true">
      {concepts.map((concept, index) => <div className={styles.concept} key={concept.badge} data-active={selected === index} aria-hidden={selected !== index} inert={selected !== index} data-tone={index}>
        <div className={styles.adPreview}>
          <div className={styles.adHead}><Image src={index === 0 ? logos.instagram : logos.facebook} alt="" width={18} height={18} unoptimized /><span>{copy.platform}</span><span>{concept.format}</span></div>
          <div className={styles.adArt} aria-hidden="true"><div className={styles.artGrid} /><div className={styles.artOrbit}><i /><i /><i /></div><div className={styles.artSculpture}><i /><i /><i /></div><span className={styles.artPlay}><Play size={16} fill="currentColor" /></span><span className={styles.artTag}>{concept.format}</span></div>
          <div className={styles.adCaption}><strong>{concept.title}</strong><span><ArrowUpRight size={18} aria-hidden="true" /></span></div>
          <div className={styles.adProgress} aria-hidden="true"><i /></div>
        </div>
        <div className={styles.creativeMetric}><small>{concept.metricLabel}</small><strong>{concept.metricValue}</strong><span>{index === 0 ? <ArrowDownRight size={15} aria-hidden="true" /> : <ArrowUpRight size={15} aria-hidden="true" />}{concept.delta}</span><div className={styles.metricPlot} aria-hidden="true"><svg viewBox="0 0 200 75" preserveAspectRatio="none"><path d={index === 0 ? "M0 8 C20 8 20 24 40 20 S65 18 80 37 S110 30 125 47 S150 42 165 63 S185 57 200 70" : "M0 35 C20 35 20 45 40 30 S65 35 80 22 S110 26 125 18 S150 25 165 16 S185 22 200 12"} /></svg></div><span className={styles.metricBadge}>{index === 0 ? <Check size={13} aria-hidden="true" /> : <FlaskConical size={13} aria-hidden="true" />}{concept.badge}</span></div>
      </div>)}
    </div>
    <div className={styles.placements}><strong>{copy.placementsTitle}</strong>{copy.placements.map(placement => <div className={styles.placement} key={placement.label}>
      <Image src={logos[placement.platform]} alt="" width={15} height={15} unoptimized /><span>{placement.label}</span><small>{placement.share}</small><strong>{placement.result}</strong>
    </div>)}</div>
    <p className={styles.demoNote}>{copy.footnote}</p>
  </div>;
}
